import type { ItemFiltersType } from '@/common/api/types/itemFilters';
import { commonQueryKeys } from '@/common/queryKeys/common';

// Named fields, not indices. `useReportingStore` lives here rather than coming from the flag,
// since a cached entry can outlive a flag flip. `__treeSet` tags the segment unambiguously — the
// filter is an object too, and probing field names instead would pick it if it ever carried both.
type TreeSetDescriptor = {
  __treeSet: true;
  order: string | undefined;
  includeDescendants: boolean;
  useReportingStore: boolean;
};

export const itemsTreeSetQueryKey = ({
  filter,
  order,
  includeDescendants = false,
  useReportingStore,
}: {
  filter?: ItemFiltersType;
  order?: string;
  includeDescendants?: boolean;
  useReportingStore: boolean;
}) =>
  [
    ...commonQueryKeys.itemsTreeSet.byFilter(filter),
    { __treeSet: true, order, includeDescendants, useReportingStore } satisfies TreeSetDescriptor,
  ] as const;

const descriptorOf = (queryKey: readonly unknown[]): TreeSetDescriptor | null => {
  for (const segment of queryKey) {
    if (typeof segment !== 'object' || segment === null) continue;
    if ('__treeSet' in segment && segment.__treeSet === true) {
      return segment as TreeSetDescriptor;
    }
  }
  return null;
};

// Strict `=== true`, not truthy, so a key this can't read matches nothing rather than everything.
export const readsReportingStoreTreeSet = ({
  queryKey,
}: {
  queryKey: readonly unknown[];
}): boolean => descriptorOf(queryKey)?.useReportingStore === true;

/** No order means priority order — the only variant an optimistic drag reorder can patch in place. */
export const isPriorityOrderedTreeSetKey = ({
  queryKey,
}: {
  queryKey: readonly unknown[];
}): boolean => {
  const descriptor = descriptorOf(queryKey);
  return descriptor !== null && !descriptor.order;
};
